import { useEffect, useRef, useCallback } from 'react';
import { api } from '../services/api';

const LOCATION_STORAGE_KEY = 'windmill_last_location';

/**
 * Custom hook for tracking location changes and reporting to API when threshold is exceeded.
 * Checks location when app regains focus and reports if user moved more than 10 miles.
 * 
 * @param {boolean} enabled - Whether location tracking is enabled (e.g., based on user preferences)
 * @returns {Function} checkLocation - Function to manually trigger location check
 */
// 10 miles in meters
const DISTANCE_THRESHOLD_METERS = 10 * 1609.34; // 10 miles = 16,093.4 meters

export function useLocationTracking(enabled = false) {
  const permissionDeniedRef = useRef(false);

  /**
   * Load last known location from localStorage
   */
  const loadLastLocation = () => {
    try {
      const stored = localStorage.getItem(LOCATION_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.debug('Error loading location from localStorage:', error);
    }
    return null;
  };

  /**
   * Save location to localStorage
   */
  const saveLocation = (latitude, longitude) => {
    try {
      localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify({ latitude, longitude }));
    } catch (error) {
      console.debug('Error saving location to localStorage:', error);
    }
  };

  /**
   * Calculate distance between two coordinates using Haversine formula
   * @param {number} lat1 
   * @param {number} lon1 
   * @param {number} lat2 
   * @param {number} lon2 
   * @returns {number} Distance in meters
   */
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  /**
   * Report location to API
   */
  const reportLocation = useCallback(async (latitude, longitude) => {
    try {
      await api.reportLocation(latitude, longitude);
      saveLocation(latitude, longitude);
    } catch (error) {
      console.error('Error reporting location:', error);
      // Don't throw - location reporting failures shouldn't break the app
    }
  }, []);

  /**
   * Check location and report if threshold exceeded
   */
  const checkLocation = useCallback(() => {
    if (!enabled) {
      return;
    }

    if (!navigator.geolocation) {
      console.debug('Geolocation not supported');
      return;
    }

    // Check permission status first
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
        if (permissionStatus.state === 'denied') {
          permissionDeniedRef.current = true;
          return;
        }
        permissionDeniedRef.current = false;
        getCurrentLocation();
      }).catch(() => {
        // Permissions API failed, proceed with location request
        getCurrentLocation();
      });
    } else {
      // Permissions API not available, proceed with location request
      getCurrentLocation();
    }

    function getCurrentLocation() {
      const options = {
        enableHighAccuracy: false, // Use less accurate but more battery-friendly mode
        timeout: 10000,
        maximumAge: 60000 // Accept cached location up to 1 minute old
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const lastLocation = loadLastLocation();
          
          // If this is the first location, report it immediately
          if (!lastLocation) {
            reportLocation(latitude, longitude);
            return;
          }

          // Calculate distance from last reported location
          const distance = calculateDistance(
            latitude,
            longitude,
            lastLocation.latitude,
            lastLocation.longitude
          );

          // If moved more than threshold, report new location
          if (distance >= DISTANCE_THRESHOLD_METERS) {
            reportLocation(latitude, longitude);
          }
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            permissionDeniedRef.current = true;
            console.debug('Location permission denied');
          } else {
            console.debug('Location error:', error);
          }
        },
        options
      );
    }
  }, [enabled, reportLocation]);

  // Load last location from localStorage on mount
  useEffect(() => {
    if (enabled) {
      const lastLocation = loadLastLocation();
      if (lastLocation) {
        console.debug('Loaded last location from localStorage:', lastLocation);
      }
    }
  }, [enabled]);

  // Set up visibility change listener to check location when app regains focus
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Defer the location check to avoid blocking the visibility change handler
        // This matches the pattern used in useRefreshOnVisible
        setTimeout(() => {
          checkLocation();
        }, 0);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also check location on initial mount if page is visible
    if (document.visibilityState === 'visible') {
      setTimeout(() => {
        checkLocation();
      }, 0);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, checkLocation]);

  return checkLocation;
}

